'use client';

import Link from 'next/link';
import { useTenantUseCases } from '@/hooks/useTenantUseCases';

export interface UseCaseGateProps {
  /** Required `job_type_code` for this feature (e.g. "PRE_INSPECTION"). */
  requiredUseCaseCode: string;
  /** Display name used in the 403 fallback screen. */
  featureName?: string;
  children: React.ReactNode;
}

function LoadingState() {
  return (
    <div
      role="status"
      aria-live="polite"
      className="flex min-h-[40vh] items-center justify-center bg-background"
    >
      <div className="text-center">
        <div className="inline-block h-10 w-10 animate-spin rounded-full border-b-2 border-primary-600" />
        <p className="mt-3 text-sm text-gray-500">Checking tenant configuration…</p>
      </div>
    </div>
  );
}

function ForbiddenScreen({ featureName }: { featureName: string }) {
  return (
    <div
      role="alert"
      data-testid="use-case-gate-forbidden"
      className="flex min-h-[50vh] items-center justify-center px-6 py-12"
    >
      <div className="max-w-md rounded-lg border border-gray-200 bg-white p-8 text-center shadow-sm">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
          <span className="text-xl font-semibold text-red-600" aria-hidden="true">
            403
          </span>
        </div>
        <h1 className="text-lg font-semibold text-gray-900">{featureName} is not enabled</h1>
        <p className="mt-2 text-sm text-gray-600">
          This feature is not enabled for your tenant. Ask your tenant administrator to
          enable it under Tenant Settings → Use Cases.
        </p>
        <Link
          href="/dashboard"
          className="mt-6 inline-flex items-center justify-center rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700"
        >
          Back to Dashboard
        </Link>
      </div>
    </div>
  );
}

export function UseCaseGate({
  requiredUseCaseCode,
  featureName = 'This feature',
  children,
}: UseCaseGateProps) {
  const { enabledCodes, isLoading, error, unscoped } = useTenantUseCases();

  if (unscoped) return <>{children}</>;

  if (isLoading) return <LoadingState />;

  // A fetch error (network blip, brief outage, laptop waking from sleep)
  // is NOT the same as the tenant genuinely not having this use case
  // enabled -- useTenantUseCases itself documents its contract as
  // fail-open on error. React Query retries/refetches on reconnect in the
  // background, so just render through rather than showing a misleading
  // "ask your tenant administrator" screen for a transient issue.
  if (error) return <>{children}</>;

  if (!enabledCodes || !enabledCodes.has(requiredUseCaseCode)) {
    return <ForbiddenScreen featureName={featureName} />;
  }

  return <>{children}</>;
}
